import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import shortid from 'shortid';

import { showStats } from 'actions/gameActions';
import * as buttonsTypes from 'consts/buttons';
import { playAudio, toggleAudio, playWatch } from 'actions/audioActions';
import { s3bucketPublicURL } from 'config';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import {
  playMobileFindTheOneStandardFeedback,
  playMobileFindTheOneAudio,
  stopMobileFindTheOneAudio,
} from 'activity-templates/find-the-one/mobile/mobileFindTheOneAudio';
import { validateQuestion, validateAnswer } from './movingAnswersHelpers';
import {
  ActivityButtons,
  ActivityImage,
  InstructionsTextBlock,
  ProblemsProgress,
  MultiplayerOverlay,
} from 'components/flink-play';

import {
  changePlayerTurn,
  incrementPlayerPoints,
} from 'actions/gameActions';

import AnswersBlock from './AnswersBlock/AnswersBlock';
import { animations as stopItAnimations } from 'activity-templates/utils';
import './MovingAnswers.scss';
import './mobile/MovingAnswers.mobile.scss';

const keepGoingAnimations = [
  'leftToRight',
  'rightToLeft',
  'topToBottom',
  'bottomToTop',
];

const formatQuestion = (question) => {
  if (!question) return null;

  return { ...question, answers: shuffleAnswers(question) };
};

const shuffleAnswers = (question) =>
  _.chain(question.incorrectAnswers)
    .filter((answer) => validateAnswer(answer))
    .map((answer) => ({ ...answer, id: shortid.generate(), correct: false }))
    .concat({ ...question.correct, id: shortid.generate(), correct: true })
    .shuffle()
    .value();

class MovingAnswers extends Component {
  constructor(props) {
    super(props);

    const { options } = props.activity.data;

    const locale = props.activity.activity?.monolingual
      ? ''
      : (
          window.location.href.indexOf('edit-activities') !== -1 &&
          props.activity.activity?.currentLanguageVersion?.locale
        )
        ? props.activity.activity.currentLanguageVersion.locale
        : props?.solutionLocale?.code
          ? props.solutionLocale.code
          : '';

    // TemplateWrapper already resolves the content version for the current
    // Activity. Reuse it instead of independently resolving the language from
    // solutionLocale (which can represent the UI/Help language, not content).
    // Retain legacy inference only when no explicit versionType was provided.
    const requestedVersionType = typeof props.versionType === 'string'
      ? props.versionType
      : ((!locale || locale === 'en') ? '' : `immersion_${locale}`);

    const localizedGameData = requestedVersionType
      ? props.activity.data.languageVersionData?.[requestedVersionType]
      : null;

    const hasLocalizedGameData = !!localizedGameData;
    const versionType = hasLocalizedGameData ? requestedVersionType : '';
    const gameData = localizedGameData || props.activity.data.gameData || {};

    console.log('[MovingAnswers][resolveGameData]', {
      locale,
      suppliedVersionType: props.versionType,
      requestedVersionType,
      effectiveVersionType: versionType,
      hasLocalizedGameData,
      hasDefaultGameData: !!props.activity.data.gameData,
      localizedProblemsCount: Array.isArray(localizedGameData?.problems)
        ? localizedGameData.problems.length
        : 0,
      defaultProblemsCount: Array.isArray(props.activity.data.gameData?.problems)
        ? props.activity.data.gameData.problems.length
        : 0,
    });

    let problems = Array.isArray(gameData.problems)
      ? gameData.problems.filter(validateQuestion)
      : [];

    const { questionsLimit, randomOrder } = options;

    problems = randomOrder ? _.shuffle(problems) : problems;

    if (
      questionsLimit &&
      questionsLimit > 0 &&
      questionsLimit < problems.length
    ) {
      problems = _.take(problems, questionsLimit);
    }

    const isKeepGoingAnimations = !gameData.longAnswers && !gameData.withAudio;
    const animations = isKeepGoingAnimations
      ? keepGoingAnimations
      : stopItAnimations;

    const maxFontSize = gameData.longAnswers && 36;

    this.state = {
      maxFontSize,
      animations: _.shuffle(animations),
      problems,
      currentQuestion: null,
      gameData,
      versionType,
    };
  }

  componentDidMount() {
    document.addEventListener('startGame', this.startGame);
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutBeforeNext);
    if (this.isNativeMobileRuntime()) {
      stopMobileFindTheOneAudio();
    }
    document.removeEventListener('audioEnded', this.setupNextQuestion);
    document.removeEventListener('startGame', this.startGame);
  }

  startGame = () => {
    const { problems, gameData } = this.state;

    const currentQuestion = formatQuestion(problems[0]);

    this.setState({
      currentQuestion,
      currentIndex: 0,
      resolved: false,
      showingCorrectAnswer: false,
      mobileSelectedAnswerId: null,
      problemsCount: problems.length,
      attemptsOnCurrentQuestion: 0,
      answeredCorrectly: 0,
      answeredIncorrectly: 0,
      isActivityImageShow: gameData.activityImageDefaultOpen || false,
      questionsLeft: problems.length,
    });

    this.playAudioQuestion(currentQuestion);
  };

  playAudioQuestion = (question) => {
    const questionToPlay = question || this.state.currentQuestion;
    const { versionType } = this.state;

    if (!questionToPlay || !questionToPlay.audio) return;

    const {
      activity: {
        activity: { contentFolder },
      },
    } = this.props;

    const audioUrl = versionType
      ? `${s3bucketPublicURL}/${contentFolder}/languageVersionData/${versionType}/${questionToPlay.audio}`
      : `${s3bucketPublicURL}/${contentFolder}/gamedata/${questionToPlay.audio}`;

    console.log('[MovingAnswers][playAudioQuestion]', {
      versionType,
      audio: questionToPlay.audio,
      audioUrl,
    });

    !question ? toggleAudio(audioUrl) : playAudio(audioUrl);
  };

  isNativeMobileRuntime = () => {
    if (!this.props.mobileLayout) return false;

    const platform = getMobileAppPlatform();
    return platform === 'ios' || platform === 'android';
  };

  isAndroidMobileRuntime = () => {
    if (!this.props.mobileLayout) return false;

    const isAndroidUserAgent =
      typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');

    return getMobileAppPlatform() === 'android' || isAndroidUserAgent;
  };

  playMobileFeedback = (feedback) => {
    stopMobileFindTheOneAudio();

    const onEnd = () => {
      if (typeof feedback.onClose === 'function') {
        feedback.onClose();
      }
    };

    if (feedback.audio) {
      const {
        activity: {
          activity: { contentFolder },
        },
      } = this.props;
      const { versionType } = this.state;
      const audioUrl = versionType
        ? `${s3bucketPublicURL}/${contentFolder}/languageVersionData/${versionType}/${feedback.audio}`
        : `${s3bucketPublicURL}/${contentFolder}/gamedata/${feedback.audio}`;

      playMobileFindTheOneAudio(audioUrl, { onEnd });
      return;
    }

    playMobileFindTheOneStandardFeedback(feedback.isCorrect, { onEnd });
  };

  finishGame = () => {
    const { answeredCorrectly, answeredIncorrectly, problems } = this.state;

    this.props.showStats({
      withScore: true,
      data: {
        allProblemsCount: problems.length,
        problemsAnsweredCorrectly: answeredCorrectly,
        problemsAnsweredIncorrectly: answeredIncorrectly,
      },
    });

    this.setState({
      isActivityImageShow: false,
      mobileSelectedAnswerId: null,
    });
  };

  setupNextQuestion = (callback) => {
    const {
      questionsLeft,
      answeredCorrectly,
      attemptsOnCurrentQuestion,
    } = this.state;
    const { delayBeforeNext } = this.props.activity.data.options;

    let newAnsweredCorrectly = answeredCorrectly;

    if (!attemptsOnCurrentQuestion) {
      newAnsweredCorrectly++;
    }

    const newQuestionsLeft = questionsLeft - 1;

    if (!newQuestionsLeft) {
      this.setState(
        {
          answeredCorrectly: newAnsweredCorrectly,
          questionsLeft: newQuestionsLeft,
          showingCorrectAnswer: false,
          mobileSelectedAnswerId: null,
          currentQuestion: null,
        },
        this.finishGame
      );
    } else {
      this.timeoutBeforeNext = setTimeout(() => {
        this.setState((state) => {
          const nextQuestion = formatQuestion(
            state.problems[state.currentIndex + 1]
          );

          this.playAudioQuestion(nextQuestion);

          return {
            answeredCorrectly: newAnsweredCorrectly,
            attemptsOnCurrentQuestion: 0,
            questionsLeft: newQuestionsLeft,
            resolved: false,
            showingCorrectAnswer: false,
            mobileSelectedAnswerId: null,
            currentQuestion: nextQuestion,
            currentIndex: state.currentIndex + 1,
          };
        });

        if (typeof callback === 'function') callback();
      }, delayBeforeNext * 1000);
    }
  };

  showAnswer = () => {
    const { multiplayerModeEnabled, incrementPlayerPoints, mobileLayout } = this.props;

    // Mobile: commit the reveal before starting "Here's the Correct Answer" audio.
    // This guarantees the correct card is visible even if its moving animation has
    // already carried it outside the Work Area. Desktop/web keeps the old flow.
    if (mobileLayout) {
      this.setState(
        (state) => ({
          resolved: true,
          showingCorrectAnswer: true,
          attemptsOnCurrentQuestion: state.attemptsOnCurrentQuestion + 1,
        }),
        () => {
          if (multiplayerModeEnabled) {
            incrementPlayerPoints(this.state.attemptsOnCurrentQuestion);
          }

          document.addEventListener('audioEnded', this.setupNextQuestion, {
            once: true,
          });
          this.props.playWatch();
        }
      );
      return;
    }

    this.setState(
      (state) => ({
        resolved: true,
        attemptsOnCurrentQuestion: state.attemptsOnCurrentQuestion + 1,
      }),
      () => {
        if (multiplayerModeEnabled) {
          incrementPlayerPoints(this.state.attemptsOnCurrentQuestion);
        }
      }
    );

    this.props.playWatch();

    document.addEventListener('audioEnded', this.setupNextQuestion, {
      once: true,
    });
  };

  answerClickHandler = (answer) => {
    const { currentQuestion, attemptsOnCurrentQuestion } = this.state;
    const {
      changePlayerTurn,
      incrementPlayerPoints,
      multiplayerModeEnabled,
      mobileLayout = false,
    } = this.props;

    if (mobileLayout) {
      // Native mobile: persist the tapped answer independently of browser focus.
      // This also freezes the moving answers while feedback is playing.
      this.setState({ mobileSelectedAnswerId: answer.id });
    }

    const feedback = { isCorrect: answer.correct };

    if (currentQuestion.specificFeedback) {
      feedback.audio = answer.feedbackAudio;
      feedback.text = answer.feedbackText;
    }

    if (answer.correct) {
      this.setState({ resolved: true, showingCorrectAnswer: false });

      if (multiplayerModeEnabled) {
        incrementPlayerPoints(attemptsOnCurrentQuestion);
      }

      feedback.onClose = () => {
        this.setupNextQuestion(() => {
          if (multiplayerModeEnabled) {
            changePlayerTurn();
          }
        });
      };
    } else {
      if (multiplayerModeEnabled) {
        changePlayerTurn();
      }

      this.setState((state) => ({
        attemptsOnCurrentQuestion: state.attemptsOnCurrentQuestion + 1,
        answeredIncorrectly:
          state.attemptsOnCurrentQuestion === 0
            ? state.answeredIncorrectly + 1
            : state.answeredIncorrectly,
      }));

      if (mobileLayout) {
        feedback.onClose = () => {
          // Resume the same answer set after wrong feedback. The answer wrappers
          // stay mounted and paused while selected, so clearing selection resumes
          // them from their existing positions instead of restarting the round.
          this.setState({ mobileSelectedAnswerId: null });
        };
      }
    }

    if (this.isNativeMobileRuntime() && !feedback.text) {
      // Native mobile must release the selected-only state even when a custom
      // feedback audio file is used. The dedicated player has end/error/load
      // watchdogs, unlike the global audioEnded path.
      this.playMobileFeedback(feedback);
      return;
    }

    this.props.showFeedback(feedback);
  };

  showActivityImage = (show) => {
    this.setState({ isActivityImageShow: show });
  };

  render() {
    const {
      resolved,
      problems,
      animations,
      maxFontSize,
      currentIndex,
      questionsLeft,
      currentQuestion,
      isActivityImageShow,
      attemptsOnCurrentQuestion,
      versionType,
      showingCorrectAnswer,
      mobileSelectedAnswerId,
    } = this.state;

    const {
      activity,
      textStyle: propsTextStyle,
      multiplayerModeEnabled,
    } = this.props;
    const { showAnswer, showQuestion } = activity.data.options;
    const { mobileLayout } = this.props;
    const isAndroidMobile = this.isAndroidMobileRuntime();

    const templateClassName = mobileLayout
      ? `moving-answers-template moving-answers-template--mobile${
          !showQuestion ? ' moving-answers-template--mobile-no-question' : ''
        }${isAndroidMobile ? ' moving-answers-template--android' : ''}`
      : 'moving-answers-template';

    const textStyle =
      maxFontSize && parseInt(propsTextStyle.fontSize) > maxFontSize
        ? { ...propsTextStyle, fontSize: maxFontSize + 'px' }
        : propsTextStyle;

    return (
      <div className={templateClassName}>
        {showQuestion && currentQuestion && (
          <InstructionsTextBlock
            className="moving-answers-question-text"
            wrapperStyle={
              mobileLayout
                ? {
                    ...(isAndroidMobile ? { top: '0' } : {}),
                    maxHeight: 'clamp(68px, 19vh, 112px)',
                    height: 'auto',
                  }
                : undefined
            }
            textStyle={textStyle}
            text={
              currentQuestion.fillIn && !resolved
                ? currentQuestion.problem.replace(
                    currentQuestion.correct.text,
                    '_______'
                  )
                : currentQuestion.problem
            }
          />
        )}

        {problems && (
          <ProblemsProgress
            problemsNumber={problems.length}
            problemsLeft={questionsLeft}
          />
        )}

        <AnswersBlock
          animation={animations && animations[currentIndex % animations.length]}
          activity={activity}
          resolved={resolved}
          multiplayer={multiplayerModeEnabled}
          question={currentQuestion}
          textStyle={textStyle}
          forceCorrectAnswerVisible={Boolean(
            this.props.mobileLayout && showingCorrectAnswer
          )}
          mobileInteraction={Boolean(this.props.mobileLayout)}
          mobileSelectedAnswerId={mobileSelectedAnswerId}
          isAndroidMobile={isAndroidMobile}
          workAreaStyle={
            isAndroidMobile && showQuestion
              ? { transform: 'translateY(12px)' }
              : undefined
          }
          answerClickHandler={this.answerClickHandler}
          versionType={versionType}
        />

        {multiplayerModeEnabled && <MultiplayerOverlay />}

        <ActivityButtons
          buttons={[
            buttonsTypes.GO_BACK,
            {
              type: buttonsTypes.ACTIVITY_IMAGE,
              onClick: () => this.showActivityImage(true),
              dontShow: !activity.data.activityImage,
            },
            {
              type: buttonsTypes.CORRECT_ANSWER,
              onClick: () => this.showAnswer(),
              dontShow:
                resolved ||
                showAnswer === 'n/a' ||
                +showAnswer > attemptsOnCurrentQuestion,
            },
            {
              type: buttonsTypes.SPEAK,
              onClick: () => this.playAudioQuestion(),
              dontShow: !currentQuestion || !currentQuestion.audio || resolved,
            },
            buttonsTypes.HELP,
          ]}
        />

        {activity.data.activityImage && (
          <ActivityImage
            closeHandler={() => this.showActivityImage(false)}
            activity={activity}
            show={isActivityImageShow}
          />
        )}
      </div>
    );
  }
}

export default connect(null, {
  playWatch,
  showStats,
  changePlayerTurn,
  incrementPlayerPoints,
})(MovingAnswers);
